import React, { useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import secret from "../secret.json";
import TopSubNavBar from "../components/TopSubNavBar";
import LevelTestCodeVault from "../features/leveltest/components/LevelTestCodeVault";
import "../components/TopNavBar.css";

// 레벨테스트 이용권을 받은 선생님께 보여 주는 따라하기 안내(2026-10-08 대표: 단톡방에 링크로 뿌린다).
//
// 로그인 없이 열린다. **화면은 실제 코드함(LevelTestCodeVault)이고 데이터만 예시다.** 서버를 부르지 않고
// 아무것도 저장하지 않는다. 화면 위의 말풍선은 public/teacher/guide-tour.js 가 띄운다.
//
// 흐름: 메뉴 → 코드 → 안내 문구 복사 → 학생 응시 화면(test-player.html?tour=1, 코드 넣고 시작 누르기까지)
//       → 돌아와서(?step=score) 점수 → 리포트 열기.

const TOUR_SCRIPT_URL = "/teacher/guide-tour.js";
const TEST_PLAYER_TOUR_URL = "/teacher/test-player.html?tour=1";
const DEMO_REPORT_URL = `${String(secret.REPORT_BASE_URL || "").replace(/\/$/, "")}/report/demo/`;

const DEMO_TAB_LIST = [
  {
    name: "worksheet",
    text: "문제지",
    subTabList: [
      { name: "list", text: "문제지 목록" },
      { name: "given", text: "출제 문제지 목록" },
      { name: "retry", text: "복습 문제지 목록" },
      { name: "leveltest", text: "레벨테스트 관리" },
    ],
  },
  { name: "academy", text: "학원 관리", subTabList: [] },
];

const dateAfterDays = (dayCount) => {
  const date = new Date();
  date.setDate(date.getDate() + dayCount);
  return date.toISOString().slice(0, 10);
};

const buildDemoData = () => {
  const expires = dateAfterDays(30);
  const guide = (code) =>
    `[지인영어학원] 레벨테스트 안내\n아래 주소에서 응시 코드 ${code} 를 넣고 시작해 주세요.`;
  const phonicsCodeList = ["A7K2M9P4", "H3R8V6WN", "M5T2C9QX", "P8D4K7ZE"];
  const levelCodeList = ["Q4WN8T2R", "B9X3F6LM", "R2Y7H5KD", "T6E9N3UJ", "W8C2P4VG"];
  return {
    level: "light",
    academy: "지인영어학원",
    is_light: false,
    demo_report_url: DEMO_REPORT_URL,
    usable: [
      ...phonicsCodeList.map((code) => ({ code, source: "파닉스 전용", expires, guide_message: guide(code) })),
      ...levelCodeList.map((code) => ({ code, source: "전체 레벨", expires, guide_message: guide(code) })),
    ],
    used: [
      {
        code: "S5F8J2KA",
        student_name: "홍길동",
        grade: "초등 5학년",
        used_at: new Date().toISOString(),
        right_count: 58,
        total_count: 75,
        report_url: DEMO_REPORT_URL,
        is_abandoned: false,
        buildable_session_id: "",
        report_building: false,
      },
    ],
  };
};

const loadTourScript = () =>
  new Promise((resolve) => {
    if (window.GuideTour) return resolve(window.GuideTour);
    const script = document.createElement("script");
    script.src = TOUR_SCRIPT_URL;
    script.onload = () => resolve(window.GuideTour);
    document.body.appendChild(script);
  });

const DemoLogo = () => (
  <span className="teacher-nav__default-logo">
    <svg viewBox="0 0 64 48" xmlns="http://www.w3.org/2000/svg" className="teacher-nav__default-logo-mark">
      <path d="M22 4a17 17 0 1 0 0 34c1.2 0 2.4-.13 3.5-.37L20 46l12-9.5A17 17 0 0 0 22 4z" fill="#171D26" />
      <path d="M42 8a15.5 15.5 0 1 1 0 31c-1 0-2-.1-3-.3L44 46l-9.5-8.3A15.5 15.5 0 0 1 42 8z" fill="#00D9C0" />
    </svg>
    <span className="teacher-nav__default-logo-text">ECHO</span>
  </span>
);

const LevelTestDemoPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const startAtScore = searchParams.get("step") === "score";
  const demoData = useMemo(buildDemoData, []);

  useEffect(() => {
    let tour = null;
    let isCurrent = true;
    const restart = () => {
      window.location.href = "/demo/leveltest";
    };
    const goMyLevelTest = () => navigate("/worksheet/leveltest");

    const firstPartList = [
      {
        target: null,
        hint: true,
        text:
          "받으신 <b>레벨테스트 이용권</b>은 지인에듀 온라인 출제에서 써요.<br>" +
          "코드를 학생에게 주고, <b>점수와 리포트</b>를 확인하는 곳까지 같이 가 볼게요.",
        nextLabel: "시작하기",
      },
      {
        target: ".teacher-sub-nav__tab--active",
        text: "지인에듀 아이디로 로그인한 뒤 <b>문제지 &gt; 레벨테스트 관리</b>를 누르세요.",
      },
      {
        target: ".code-vault__stat--accent",
        text: "받은 응시코드가 여기 들어 있어요. <b>학생 한 명당 한 장</b>이에요.",
      },
      {
        target: ".code-vault__card",
        text: "코드마다 카드가 있어요. <b>파닉스 전용</b>과 <b>전체 레벨</b>이 따로 표시되고, 쓸 수 있는 날짜도 보여요.",
      },
      {
        target: ".code-vault__card .code-vault__button",
        text: "<b>안내 문구 복사</b>를 눌러 학부모님께 그대로 붙여 넣으면 끝이에요.",
      },
      {
        target: null,
        text: "이제 학생 화면으로 가 볼게요. 학생은 받은 링크에서 <b>이름, 학년, 응시 코드</b>를 넣고 시작해요.",
        nextLabel: "학생 화면 보기",
        next: () => {
          window.location.href = TEST_PLAYER_TOUR_URL;
        },
      },
    ];

    const scorePartList = [
      {
        target: ".code-vault__table tbody tr",
        hint: true,
        text: "학생이 시험을 마치면 <b>학생 리포트</b>에 이름과 응시일이 올라와요.",
      },
      {
        target: ".code-vault__score",
        text: "<b>점수</b>는 여기예요. 맞힌 문항 수 / 전체 문항 수예요.",
      },
      {
        target: ".code-vault__table tbody tr td:last-child .code-vault__button",
        text: "<b>열기</b>를 누르면 리포트가 새 창으로 열려요. 학부모님께 링크를 그대로 보내셔도 돼요.",
        extraButton: {
          label: "리포트 열어 보기",
          onClick: () => window.open(DEMO_REPORT_URL, "_blank", "noopener"),
        },
      },
      {
        target: null,
        text: "이렇게 쓰시면 돼요. 이제 <b>내 레벨테스트 관리</b>에서 받은 코드를 확인해 보세요.",
        extraButton: { label: "처음부터 다시", onClick: restart },
        nextLabel: "내 화면으로 가기",
        next: goMyLevelTest,
      },
    ];

    loadTourScript().then((GuideTour) => {
      if (!isCurrent || !GuideTour) return;
      tour = GuideTour.run(startAtScore ? scorePartList : firstPartList, {});
    });
    return () => {
      isCurrent = false;
      if (tour) tour.close();
    };
  }, [navigate, startAtScore]);

  return (
    <div
      style={{
        fontFamily: "'Pretendard', 'Spoqa Han Sans Neo', 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif",
        backgroundColor: "#F5F8F8",
        letterSpacing: "-0.01em",
        minHeight: "100vh",
      }}
    >
      <div className="teacher-nav">
        <div className="teacher-nav__inner">
          <div className="teacher-nav__side">
            <DemoLogo />
          </div>
          <div className="teacher-nav__tabs">
            {DEMO_TAB_LIST.map((tab) => (
              <span
                key={tab.name}
                className={`teacher-nav__tab${tab.name === "worksheet" ? " teacher-nav__tab--active" : ""}`}
              >
                {tab.text}
                {tab.name === "worksheet" && <span className="teacher-nav__tab-underline" />}
              </span>
            ))}
          </div>
          <div className="teacher-nav__side teacher-nav__side--right" />
        </div>
      </div>
      <TopSubNavBar topTab="worksheet" subTab="leveltest" selectSubTab={() => {}} tabList={DEMO_TAB_LIST} />
      <LevelTestCodeVault demoData={demoData} />
    </div>
  );
};

export default LevelTestDemoPage;
